import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Calculator, TrendingUp, Users, ArrowRight, ShieldCheck } from "lucide-react";

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(
    isFinite(n) ? Math.round(n) : 0
  );

// Assumptions:
// - 20% of customers are regulars who visit 2x more than the average customer.
//   Their share of revenue = (0.2 * 2) / (0.2 * 2 + 0.8 * 1) = 0.4 / 1.2 = 33.3%
// - A loyalty program lifts repeat visit frequency by 15%, applied to loyal-customer revenue.
const LOYAL_SHARE = 0.4 / 1.2;
const UPLIFT_RATE = 0.15;

export default function RevenueCalculator() {
  const [revenue, setRevenue] = useState(40000);

  const R = Number(revenue) || 0;
  const loyalMonthly = R * LOYAL_SHARE;
  const upliftMonthly = loyalMonthly * UPLIFT_RATE;
  const annualGain = upliftMonthly * 12;

  const results = [
    { value: loyalMonthly, label: "Current loyal customer revenue / month", color: "text-[#17324D]", sub: "≈ 20% of customers, visiting twice as often" },
    { value: upliftMonthly, label: "Projected monthly uplift with a loyalty program", color: "text-[#17324D]", prefix: "+", sub: "+15% repeat visit frequency" },
    { value: annualGain, label: "Estimated annual gain", color: "text-[#D94A3D]", prefix: "+", sub: "Extra revenue over 12 months" },
  ];

  return (
    <div className="App bg-[#FFF4E6] text-[#17324D] min-h-screen">
      <Navbar />

      <section className=" border-b border-[#17324D]/15">
        <div className="max-w-5xl mx-auto px-5 py-16 md:py-20">
          <motion.span
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase bg-[#17324D] text-white px-3 py-1.5 rounded-xl mb-6 border border-[#17324D]/15">
            <Calculator size={14} /> Revenue Calculator
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="font-heading font-extrabold text-3xl md:text-7xl leading-[0.9] tracking-tight">
            See what regulars<br /><span className="text-[#D94A3D]">are worth</span>.
          </motion.h1>
          <p className="mt-5 text-lg text-[#17324D]/80 max-w-xl">
            Enter your restaurant's estimated monthly revenue and see how much a loyalty program could add to your bottom line.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 py-14 grid md:grid-cols-2 gap-10 items-start">
        {/* Input card */}
        <div data-testid="calc-input-card" className="card-brut-lg rounded-lg bg-white p-8">
          <label htmlFor="revenue" className="flex items-center gap-2 font-heading font-bold text-lg">
            <Users size={22} className="text-[#17324D]" /> Monthly revenue
          </label>
          <p className="text-sm text-[#17324D]/60 mt-1">Your restaurant's estimated total sales per month.</p>

          <div className="mt-5 flex items-center border border-[#17324D]/15 rounded-xl bg-[#FFF4E6] overflow-hidden focus-within:ring-2 ring-[#D94A3D]/40">
            <span className="px-4 py-3 bg-[#17324D] text-white font-heading font-bold text-lg">$</span>
            <input
              id="revenue"
              data-testid="revenue-input"
              type="number"
              min="0"
              step="1000"
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              className="flex-1 min-w-0 px-4 py-3 bg-transparent text-2xl font-heading font-bold focus:outline-none"
              placeholder="40000"
            />
          </div>

          <input
            data-testid="revenue-slider"
            type="range"
            min="0"
            max="200000"
            step="1000"
            value={Math.min(Number(revenue) || 0, 200000)}
            onChange={(e) => setRevenue(e.target.value)}
            className="w-full mt-6 accent-[#D94A3D] cursor-pointer"
          />
          <div className="flex justify-between text-xs text-[#17324D]/50 mt-1">
            <span>$0</span><span>$200k+</span>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {[20000, 40000, 75000, 120000].map((v) => (
              <button
                key={v}
                data-testid={`preset-${v}`}
                onClick={() => setRevenue(v)}
                className="text-sm font-semibold px-3 py-1.5 border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6]"
              >
                {usd(v)}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="space-y-5">
          {results.map((r, i) => (
            <motion.div
              key={i}
              data-testid={`result-card-${i}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="card-brut rounded-xl bg-white p-6"
            >
              <p className={`font-heading font-extrabold text-3xl md:text-6xl tracking-tight leading-none ${r.color}`}>
                {r.prefix || ""}{usd(r.value)}
              </p>
              <p className="font-semibold text-[#17324D] mt-3">{r.label}</p>
              <p className="text-sm text-[#17324D]/60">{r.sub}</p>
            </motion.div>
          ))}

          <Link
            to="/register"
            data-testid="calc-cta"
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D94A3D] text-white font-bold uppercase tracking-wider border border-[#17324D]/15 rounded-xl hover:bg-[#C33E32]"
          >
            Start Your Free Trial <ArrowRight size={18} />
          </Link>
          <p className="text-xs text-[#17324D]/50 flex items-center gap-1.5 justify-center">
            <TrendingUp size={13} className="text-[#17324D]" /> Estimates only — actual results depend on your menu, pricing, and members.
          </p>
        </div>
      </section>

      <footer className="bg-[#17324D] text-[#FFF4E6] border-t border-[#17324D]/15 mt-6">
        <div className="max-w-5xl mx-auto px-5 py-8 flex flex-col sm:flex-row justify-between gap-3 text-sm">
          <Link to="/" data-testid="calc-footer-home" className="font-heading font-bold text-base">THE REGULARS CLUB</Link>
          <span className="flex items-center gap-1.5 text-white/60"><ShieldCheck size={13} className="text-[#D94A3D]" /> Payments secured by Stripe</span>
        </div>
      </footer>
    </div>
  );
}
